<template>
  <!--
    یک ردیف فیلتر برای کل صفحه — نه یک فیلتر داخل هر کارت.
    همه‌ی نمودارها و جدول‌های صفحه با همین برش رندر می‌شوند.
  -->
  <b-card
    no-body
    class="report-filter mb-1"
  >
    <b-card-body class="py-1">
      <div class="d-flex flex-wrap align-items-end report-filter__row">
        <!-- پریست‌های بازه -->
        <div class="report-filter__presets">
          <label class="report-filter__label">بازه</label>
          <div class="d-flex flex-wrap">
            <b-button
              v-for="item in presets"
              :key="item.key"
              size="sm"
              class="mr-50 mb-25"
              :variant="preset === item.key ? 'primary' : 'flat-secondary'"
              @click="$emit('preset', item.key)"
            >
              {{ item.title }}
            </b-button>
          </div>
        </div>

        <!-- بازه‌ی دلخواه -->
        <div class="report-filter__dates d-flex">
          <div class="mr-1">
            <label class="report-filter__label">از تاریخ</label>
            <date-picker
              :value="value.FromDate"
              format="YYYY-MM-DD"
              display-format="jYYYY/jMM/jDD"
              :max="value.ToDate"
              @input="update('FromDate', $event)"
            />
          </div>
          <div>
            <label class="report-filter__label">تا تاریخ</label>
            <date-picker
              :value="value.ToDate"
              format="YYYY-MM-DD"
              display-format="jYYYY/jMM/jDD"
              :min="value.FromDate"
              @input="update('ToDate', $event)"
            />
          </div>
        </div>

        <!-- حالت مقایسه -->
        <div class="report-filter__compare">
          <label class="report-filter__label">مقایسه با</label>
          <b-form-select
            :value="value.Compare"
            :options="compareOptions"
            size="sm"
            @change="update('Compare', $event)"
          />
        </div>

        <!-- بازه‌ی مقایسه‌ی دلخواه، فقط وقتی حالت Custom است -->
        <div
          v-if="value.Compare === 3"
          class="report-filter__dates d-flex"
        >
          <div class="mr-1">
            <label class="report-filter__label">مقایسه از</label>
            <date-picker
              :value="value.CompareFromDate"
              format="YYYY-MM-DD"
              display-format="jYYYY/jMM/jDD"
              @input="update('CompareFromDate', $event)"
            />
          </div>
          <div>
            <label class="report-filter__label">مقایسه تا</label>
            <date-picker
              :value="value.CompareToDate"
              format="YYYY-MM-DD"
              display-format="jYYYY/jMM/jDD"
              :min="value.CompareFromDate"
              @input="update('CompareToDate', $event)"
            />
          </div>
        </div>

        <!-- بازه‌بندی محور زمان -->
        <div
          v-if="showInterval"
          class="report-filter__interval"
        >
          <label class="report-filter__label">بازه‌بندی</label>
          <b-form-select
            :value="value.Interval"
            :options="intervalOptions"
            size="sm"
            @change="update('Interval', $event)"
          />
        </div>

        <!-- فقط پرداخت‌شده -->
        <div
          v-if="showPaidOnly"
          class="report-filter__toggle"
        >
          <b-form-checkbox
            :checked="value.PaidOnly"
            switch
            class="mt-50"
            @change="update('PaidOnly', $event)"
          >
            <span class="text-nowrap">فقط پرداخت‌شده</span>
          </b-form-checkbox>
        </div>

        <div class="flex-grow-1" />

        <div class="d-flex align-items-center">
          <b-spinner
            v-if="busy"
            small
            variant="primary"
            class="mr-1"
            label="در حال بارگذاری"
          />
          <b-button
            size="sm"
            variant="outline-primary"
            @click="$emit('refresh')"
          >
            <feather-icon icon="RefreshCwIcon" size="14" />
            <span class="ml-25">به‌روزرسانی</span>
          </b-button>
        </div>
      </div>

      <!-- توضیح بازه‌ی مقایسه، تا کاربر بداند دقیقاً با چه چیزی مقایسه می‌کند -->
      <div
        v-if="compareLabel"
        class="report-filter__hint mt-75"
      >
        <feather-icon icon="GitCompareIcon" size="13" />
        <span class="ml-25">دوره‌ی مقایسه: {{ compareLabel }}</span>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import { BCardBody, BFormSelect } from 'bootstrap-vue'

export default {
  name: 'ReportFilterBar',
  components: { BCardBody, BFormSelect },
  props: {
    // فیلتر مشترک صفحه (data.filter از میکسین report)
    value: {
      type: Object,
      required: true,
    },
    preset: {
      type: String,
      default: 'last30',
    },
    busy: {
      type: Boolean,
      default: false,
    },
    showPaidOnly: {
      type: Boolean,
      default: true,
    },
    showInterval: {
      type: Boolean,
      default: true,
    },
    // برچسب بازه‌ی مقایسه که بک‌اند برگردانده
    compareLabel: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      presets: [
        { key: 'today', title: 'امروز' },
        { key: 'yesterday', title: 'دیروز' },
        { key: 'last7', title: '۷ روز' },
        { key: 'last30', title: '۳۰ روز' },
        { key: 'last90', title: '۹۰ روز' },
        { key: 'thisMonth', title: 'این ماه' },
        { key: 'lastMonth', title: 'ماه قبل' },
        { key: 'thisYear', title: 'امسال' },
        { key: 'lastYear', title: 'سال قبل' },
      ],
      // مقادیر با enum بک‌اند (ReportModel.CompareMode) یکی هستند
      compareOptions: [
        { value: 0, text: 'بدون مقایسه' },
        { value: 1, text: 'دوره‌ی قبل' },
        { value: 2, text: 'سال قبل' },
        { value: 3, text: 'بازه‌ی دلخواه' },
      ],
      intervalOptions: [
        { value: null, text: 'خودکار' },
        { value: 0, text: 'روزانه' },
        { value: 1, text: 'هفتگی' },
        { value: 2, text: 'ماهانه' },
        { value: 3, text: 'فصلی' },
        { value: 4, text: 'سالانه' },
      ],
    }
  },
  methods: {
    /**
     * تغییر فیلتر را به‌صورت یک آبجکت تازه بالا می‌فرستد تا والد
     * کنترل کامل داشته باشد (بدون mutate کردن prop).
     */
    update(key, val) {
      this.$emit('input', { ...this.value, [key]: val })
      this.$emit('change', key)
    },
  },
}
</script>

<style lang="scss" scoped>
.report-filter {
  &__row > * {
    margin-bottom: 0.5rem;
  }

  &__label {
    display: block;
    font-size: 0.75rem;
    color: #b9b9c3;
    margin-bottom: 0.25rem;
  }

  &__presets {
    margin-left: 1.25rem;
    max-width: 26rem;
  }

  &__dates,
  &__compare,
  &__interval,
  &__toggle {
    margin-left: 1.25rem;
  }

  &__compare,
  &__interval {
    min-width: 8.5rem;
  }

  &__hint {
    font-size: 0.75rem;
    color: #b9b9c3;
  }

  // دیت‌پیکر پیش‌فرض عرض کامل می‌گیرد؛ در نوار فیلتر جمعش می‌کنیم
  ::v-deep .vpd-input-group input {
    width: 7.5rem;
    height: calc(1.5em + 0.75rem + 2px);
    padding: 0.25rem 0.5rem;
    font-size: 0.857rem;
  }
}
</style>
